import { useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Dialog } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { useLanguageStore } from '../../stores/language-store';

type GuideStep = {
  src: string;
  alt: string;
};

interface GuideDialogProps {
  open: boolean;
  onClose: () => void;
  stepIndex: number;
  setStepIndex: (next: number | ((prev: number) => number)) => void;
}

export function GuideDialog({ open, onClose, stepIndex, setStepIndex }: GuideDialogProps) {
  const language = useLanguageStore((s) => s.language);
  const steps = useMemo<GuideStep[]>(
    () => [
      { src: '/docs/11.png', alt: language === 'ru' ? 'Гайд: шаг 1' : 'Guide: step 1' },
      { src: '/docs/22.png', alt: language === 'ru' ? 'Гайд: шаг 2' : 'Guide: step 2' },
      { src: '/docs/33.png', alt: language === 'ru' ? 'Гайд: шаг 3' : 'Guide: step 3' },
    ],
    [language]
  );

  const step = steps[stepIndex];

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') setStepIndex((i) => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setStepIndex((i) => Math.min(steps.length - 1, i + 1));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, steps.length, setStepIndex]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={language === 'ru' ? 'Гайд' : 'Guide'}
      panelClassName="max-w-4xl"
    >
      <div className="space-y-3">
        <div className="text-xs text-text-muted">
          {language === 'ru'
            ? `Шаг ${stepIndex + 1} из ${steps.length} (стрелки ← →)`
            : `Step ${stepIndex + 1} of ${steps.length} (use ← →)`}
        </div>
        <div className="rounded-md border border-surface-lighter bg-surface overflow-hidden">
          <img
            src={step.src}
            alt={step.alt}
            className="block w-full h-auto max-h-[70vh] object-contain"
            loading="lazy"
          />
        </div>
        <div className="flex items-center justify-between gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
            disabled={stepIndex === 0}
            title={language === 'ru' ? 'Назад' : 'Back'}
          >
            <ChevronLeft size={14} />
            <span>{language === 'ru' ? 'Назад' : 'Back'}</span>
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setStepIndex((i) => Math.min(steps.length - 1, i + 1))}
            disabled={stepIndex === steps.length - 1}
            title={language === 'ru' ? 'Далее' : 'Next'}
          >
            <span>{language === 'ru' ? 'Далее' : 'Next'}</span>
            <ChevronRight size={14} />
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
